import React, { FC, useCallback, useRef } from 'react';

import classNames from 'classnames';

import {
	BUTTON_TYPES,
	EvIcon,
	EvPopup,
	EvSelectNew,
	EvTag,
	OptionType,
	TAG_STATUS,
	TITLE_MODES
} from '@evinced-private/ui-common';

import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import { Logger } from 'src/common/services/Logger';
import ArrowRightIcon from 'src/resources/icons/ArrowRight.svg?raw';

import { RemoveIconButton } from '../RemoveIconButton';
import { UpdateTestsModalProps } from '../shared/types';

import { useLabelsManagement } from './hooks/useLabelsManagement';
import {
	CANCEL_BUTTON_TEXT,
	getModalTitle,
	MODAL_DESCRIPTION,
	SAVE_BUTTON_TEXT
} from './constants';
import { handleSave } from './handlers';

import './UpdateTestsModal.scss';

export const UpdateTestsModal: FC<UpdateTestsModalProps> = ({
	isOpen,
	onClose,
	testRows,
	tenantConfiguredLabels,
	onSaveSuccess,
	onClearSelection
}) => {
	const {
		selectedLabel,
		setSelectedLabel,
		selectedNewValue,
		setSelectedNewValue,
		changes,
		labelOptions,
		valueOptions,
		removeChange,
		resetChanges
	} = useLabelsManagement({ testRows, tenantConfiguredLabels });

	const ariaLiveRef = useRef<HTMLDivElement>(null);
	const labelSelectRef = useRef<HTMLInputElement>(null);

	const handleCloseAction = (): void => {
		resetChanges();
		onClose();
	};

	const handleClearSelections = (): void => {
		resetChanges();
		setSelectedLabel([]);
		setSelectedNewValue([]);
		onClearSelection?.();
	};

	const handleSaveAction = async (): Promise<void> => {
		try {
			await handleSave(
				testRows,
				changes,
				handleCloseAction,
				handleClearSelections,
				onSaveSuccess,
				tenantConfiguredLabels
			);
		} catch (error) {
			Logger.error('Error saving label updates in UpdateTestsModal:', error);
			handleCloseAction();
		}
	};

	const handleRemoveChange = useCallback(
		(index: number, oldLabel: string) => {
			removeChange(index);
			// Announce removal
			if (ariaLiveRef.current) {
				ariaLiveRef.current.textContent = `${oldLabel} removed from selection`;
			}
		},
		[removeChange]
	);

	const handleValueChange = useCallback(
		(values: OptionType[]) => {
			setSelectedNewValue(values);
			// After setting new value, announce the change
			if (values.length > 0 && selectedLabel.length > 0 && ariaLiveRef.current) {
				ariaLiveRef.current.textContent = `${selectedLabel[0].label} changed to ${values[0].label}`;
			}
		},
		[selectedLabel, setSelectedNewValue]
	);

	const handleLabelChange = useCallback(
		(values: OptionType[]) => {
			setSelectedLabel(values);
			// After selection, focus returns to first select
			setTimeout(() => {
				labelSelectRef.current?.focus();
			}, 0);
		},
		[setSelectedLabel]
	);

	const buttons = [
		{
			title: CANCEL_BUTTON_TEXT,
			onClick: handleCloseAction,
			type: BUTTON_TYPES.SECONDARY
		},
		{
			title: SAVE_BUTTON_TEXT,
			onClick: handleSaveAction
		}
	];

	return (
		<EvPopup
			appElement={PLATFORM_APP_ID}
			isOpen={isOpen}
			onClose={handleCloseAction}
			className={classNames('update-tests-modal')}
			popupWidth="600px"
			title={getModalTitle(testRows)}
			titleMode={TITLE_MODES.MAIN}
			buttons={buttons}
			isCentered={true}
			isControlled
			isBgTransparent={true}
		>
			<div ref={ariaLiveRef} aria-live="polite" aria-atomic="true" className="sr-only" />
			<div className="update-tests-modal__content">
				<p id="update-tests-modal-description" className="update-tests-modal__description">
					{MODAL_DESCRIPTION}
				</p>

				<div className="update-tests-modal__selects-container">
					<div className="update-tests-modal__select-wrapper">
						<EvSelectNew
							id="label-select"
							label="Choose Label"
							options={labelOptions}
							selectedValue={selectedLabel}
							onSelectChange={handleLabelChange}
							placeholder="Select a label"
							listboxWidth="100%"
							showTooltip={true}
							tooltipContent="Select which label you want to update"
							shouldSortOptions={true}
						/>
					</div>

					<div className="update-tests-modal__arrow-separator">
						<EvIcon icon={ArrowRightIcon} />
					</div>

					<div className="update-tests-modal__select-wrapper">
						<EvSelectNew
							id="value-select"
							label="New Value"
							options={valueOptions}
							selectedValue={selectedNewValue}
							onSelectChange={handleValueChange}
							placeholder="Select new value"
							listboxWidth="100%"
							isDisabled={selectedLabel.length === 0}
							showTooltip={true}
							tooltipContent="Select the new value for the label"
							shouldSortOptions={true}
						/>
					</div>
				</div>

				{changes.length > 0 && (
					<>
						<div className="update-tests-modal__divider" />

						<div className="update-tests-modal__changes-section">
							<h3 className="update-tests-modal__changes-title">Labels to change</h3>
							<div className="update-tests-modal__changes-list">
								{changes.map((change, idx) => {
									const oldLabelParts = change.oldLabelValue.split(':');
									const newLabelParts = change.newLabelValue.split(':');
									const oldLabelDisplay =
										oldLabelParts.length > 1 ? oldLabelParts[1] : oldLabelParts[0];
									const newLabelDisplay =
										newLabelParts.length > 1 ? newLabelParts[1] : newLabelParts[0];
									const oldLabelKey = oldLabelParts[0];

									return (
										<div
											key={`${change.oldLabelValue}-${change.newLabelValue}`}
											className="update-tests-modal__change-item"
										>
											<span className="update-tests-modal__change-item-old-label">
												{oldLabelKey}: {oldLabelDisplay}
											</span>
											<span className="update-tests-modal__change-item-arrow">→</span>
											<EvTag
												status={TAG_STATUS.INFO}
												text={`${oldLabelKey}: ${newLabelDisplay}`}
												className="update-tests-modal__change-item-tag"
											/>
											<RemoveIconButton
												onClick={() =>
													handleRemoveChange(idx, `${oldLabelKey}: ${oldLabelDisplay}`)
												}
												ariaLabel={`Remove from selection Update: ${oldLabelKey}: ${oldLabelDisplay} to ${oldLabelKey}: ${newLabelDisplay}`}
												className="update-tests-modal__change-item-remove-btn"
											/>
										</div>
									);
								})}
							</div>
						</div>
					</>
				)}
			</div>
		</EvPopup>
	);
};
